/**
 * Deferred async resolution for related domain objects (e.g. folder contents, linked agent).
 */
export class Lazy<T> {
  private _value?: T;
  private _loaded = false;
  private readonly _loader: () => Promise<T>;

  constructor(loader: () => Promise<T>, preloaded?: T) {
    this._loader = loader;
    if (preloaded !== undefined) {
      this._value = preloaded;
      this._loaded = true;
    }
  }

  async get(): Promise<T> {
    if (this._loaded === false) {
      this._value = await this._loader();
      this._loaded = true;
    }
    const value = this._value;
    if (value === undefined) {
      throw new Error('Lazy value is undefined after load');
    }
    return value;
  }

  isLoaded(): boolean {
    return this._loaded;
  }

  peek(): T | undefined {
    return this._value;
  }

  /**
   * Sets the resolved value synchronously (e.g. wiring a forward-ref after construction).
   * Subsequent {@link Lazy.get} returns this value without invoking the loader.
   */
  preload(value: T): void {
    this._value = value;
    this._loaded = true;
  }

  /**
   * Value is available immediately; {@link Lazy.get} returns it without invoking the loader.
   */
  static resolved<T>(value: T): Lazy<T> {
    return new Lazy(async () => value, value);
  }
}
